import { distributeSearchResults } from './globalSearch.utils';
import useAuth from 'hooks/useAuth';
import { useAxiosQuery } from 'hooks/useAxiosQuery';
import { useInfiniteAxiosQuery } from 'hooks/useInfiniteAxiosQuery';
import { ASSETS_CACHE_KEY, PROJECTS_CACHE_KEY } from 'services/queryKeys';
import { GreenerUnit, Project, ProjectCardData } from 'types';

export const useGlobalSearch = ({
  searchTerm,
  totalLimit = 9,
  page = 1,
}: {
  searchTerm?: string;
  totalLimit?: number;
  page?: number;
}) => {
  const { user } = useAuth();
  const organisationId = user?.organisation.organisationId;

  const projectsFilter = new URLSearchParams({
    ...(searchTerm
      ? {
          name__contains: searchTerm,
        }
      : {}),
  }).toString();

  const bookingsFilter = new URLSearchParams({
    ...(searchTerm
      ? {
          conjunction: 'or',
          greener_unit__name__contains: searchTerm,
          greener_unit__external_identifier__contains: searchTerm,
        }
      : {}),
  }).toString();

  const assetsFilter = new URLSearchParams({
    ...(searchTerm
      ? {
          conjunction: 'or',
          name__contains: searchTerm,
          external_identifier__contains: searchTerm,
        }
      : {}),
  }).toString();

  const {
    data: assets = [],
    isFetching: isAssetsFetching,
    ...rest
  } = useAxiosQuery<GreenerUnit[]>(ASSETS_CACHE_KEY(searchTerm), `/greener-units/?${assetsFilter}`, {
    enabled: !!searchTerm,
    suspense: false,
  });

  const { data: projects = {}, isFetching: isProjectsFetching } = useInfiniteAxiosQuery<Project>({
    key: PROJECTS_CACHE_KEY(searchTerm),
    path: `/organisations/${organisationId}/projects/?${projectsFilter}`,
    perPage: totalLimit,
    page,
    reactQueryConfig: { enabled: !!searchTerm, suspense: false },
  });

  const { data: bookings = {}, isFetching: isBookingsFetching } = useInfiniteAxiosQuery<ProjectCardData>({
    key: ASSETS_CACHE_KEY(searchTerm, organisationId?.toString()),
    path: `/organisations/${organisationId}/projects/?${bookingsFilter}`,
    perPage: totalLimit,
    page,
    reactQueryConfig: { enabled: !!searchTerm, suspense: false },
  });

  const distributedData = distributeSearchResults({
    searchResults: {
      assets,
      projects: projects?.perPageData ?? [],
      projectsWithBookedAssets: bookings?.perPageData ?? [],
    },
    totalLimit,
    searchTerm,
  });

  return {
    data: distributedData,
    isFetching: isAssetsFetching || isProjectsFetching || isBookingsFetching,
    ...rest,
  };
};
